/**
 * RecordDrawModal — finance / admin / the sales director record a draw for someone directly:
 * no request, approved at once, and say how it was paid. Calls commission_draw_record
 * (migration 20261028p_commission_draw_direct).
 *
 *   company  — paid from the company's bank: lands in "Awaiting disbursement" for Shannon.
 *   personal — paid out of a team member's own pocket (Chad, Jennifer, …: the lender is picked from finance_settings
 *              draw_lenders, the people with a reimbursements account in Books): Books adds it to THEIR reimbursements.
 *   owed     — the person already owes the company this (an earlier advance, e.g. an onboarding balance).
 *
 * Either way the draw comes off the person's next payout when Books pays it (Bill pay nets open draws).
 */

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useAuth } from "../../context/AuthContext";

type PaidHow = "company" | "personal" | "owed";

const HOW: { value: PaidHow; label: string; hint: string }[] = [
  { value: "company", label: "From the company's bank", hint: "Shannon pays it and marks it paid under Awaiting disbursement." },
  { value: "personal", label: "Paid personally by a team member", hint: "Books adds it to that person's reimbursements; the company pays them back." },
  { value: "owed", label: "Already owed to the company", hint: "An earlier advance (e.g. an onboarding balance). Nothing is paid now." },
];

function todayEastern(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
}

export default function RecordDrawModal({
  members,
  vendors,
  onClose,
  onRecorded,
}: {
  members: Record<string, { full_name: string }>;
  vendors: Record<string, { name: string }>;
  onClose: () => void;
  onRecorded?: () => void;
}) {
  const { member } = useAuth();
  const [who, setWho] = useState("");
  const [lenderIds, setLenderIds] = useState<string[] | null>(null);
  const [lender, setLender] = useState("");
  const [amount, setAmount] = useState("");
  const [how, setHow] = useState<PaidHow>("company");
  const [paidOn, setPaidOn] = useState(todayEastern());
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const people = useMemo(
    () => Object.entries(members).map(([id, m]) => ({ id, name: m.full_name })).sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  );
  const crews = useMemo(
    () => Object.entries(vendors).map(([id, v]) => ({ id, name: v.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [vendors],
  );

  // who may lend: finance_settings draw_lenders (the people with a reimbursements account in Books)
  useEffect(() => {
    let cancelled = false;
    supabase.from("finance_settings").select("value_jsonb").eq("key", "draw_lenders").maybeSingle().then(({ data }) => {
      if (cancelled) return;
      const v = (data?.value_jsonb ?? []) as unknown;
      setLenderIds(Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
    });
    return () => { cancelled = true; };
  }, []);
  const lenders = useMemo(() => {
    const ids = lenderIds && lenderIds.length > 0 ? lenderIds : Object.keys(members);
    return ids.filter((id) => members[id]).map((id) => ({ id, name: members[id].full_name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [lenderIds, members]);
  // start on the person recording it when they can lend, else on nobody (they pick)
  const lenderValue = lender || (member?.id && lenders.some((l) => l.id === member.id) ? member.id : "");

  async function save() {
    const amt = parseFloat(amount);
    if (!who) return setError("Pick who the draw is for.");
    if (!amt || amt <= 0) return setError("Amount required.");
    if (reason.trim().length < 5) return setError("Say what it is for (min 5 characters).");
    if (how === "personal" && !lenderValue) return setError("Pick who lent the money.");
    setError(null);
    setSaving(true);
    try {
      const [kind, id] = who.split(":");
      const { error: rpcErr } = await supabase.rpc("commission_draw_record", {
        p_team_member: kind === "t" ? id : null,
        p_vendor: kind === "v" ? id : null,
        p_amount: amt,
        p_paid_how: how,
        p_paid_on: how === "company" ? null : paidOn,
        p_reference: how === "company" ? null : reference.trim() || null,
        p_reason: reason.trim(),
        p_paid_by: how === "personal" ? lenderValue : null,
      });
      if (rpcErr) throw rpcErr;
      onRecorded?.();
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-base font-semibold text-bardin-ink">Record a draw</h2>
          <p className="text-xs text-slate-500">
            No request needed — it is approved now. It comes off the person's next payout when Books pays it.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Who</label>
            <select value={who} onChange={(e) => setWho(e.target.value)} className="w-full text-sm border border-slate-300 rounded px-3 py-2">
              <option value="">Pick a person or crew…</option>
              <optgroup label="Team">
                {people.map((p) => <option key={p.id} value={`t:${p.id}`}>{p.name}</option>)}
              </optgroup>
              <optgroup label="Crews">
                {crews.map((v) => <option key={v.id} value={`v:${v.id}`}>{v.name}</option>)}
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Amount</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <div className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">How was it paid?</div>
            <div className="space-y-2">
              {HOW.map((h) => (
                <label key={h.value} className={"flex gap-2 items-start rounded-lg border px-3 py-2 cursor-pointer " + (how === h.value ? "border-bardin-orange bg-orange-50/40" : "border-slate-200")}>
                  <input type="radio" name="paid-how" value={h.value} checked={how === h.value} onChange={() => setHow(h.value)} className="mt-1" />
                  <span>
                    <span className="block text-sm font-medium text-bardin-ink">{h.label}</span>
                    <span className="block text-[11px] text-slate-500">{h.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {how === "personal" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Who lent the money?</label>
              <select value={lenderValue} onChange={(e) => setLender(e.target.value)} className="w-full text-sm border border-slate-300 rounded px-3 py-2">
                <option value="">Pick the person…</option>
                {lenders.map((l) => <option key={l.id} value={l.id}>{l.name} — reimbursements</option>)}
              </select>
              <div className="text-[11px] text-slate-500 mt-1">Shannon pays it back to them from their reimbursements, not as commission.</div>
            </div>
          )}

          {how !== "company" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
                  {how === "personal" ? "Date it was paid" : "Date"}
                </label>
                <input type="date" value={paidOn} max={todayEastern()} onChange={(e) => setPaidOn(e.target.value)} className="w-full text-sm border border-slate-300 rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Reference</label>
                <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" className="w-full text-sm border border-slate-300 rounded px-3 py-2" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
              What it's for <span className="text-red-600">(required)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Onboarding balance left to repay; cash I gave him on 10/24"
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
          </div>

          {error && <div className="text-xs text-red-700 bg-red-50 rounded px-3 py-2">⚠ {error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} disabled={saving} className="px-4 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !who || !amount || reason.trim().length < 5}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Record the draw"}
          </button>
        </div>
      </div>
    </div>
  );
}
